<script setup lang="ts">
import { computed } from 'vue'
import { avatarText, getAvatarColor } from '../chatModules'
import AuthenticatedVImg from './AuthenticatedVImg.vue'
import { useGroupRoomAvatar } from '@/modules/chat-matrix-fast/composables/useGroupRoomAvatar'
import { type ChatOut, isEventChat, isGroupChat, isGroupLikeChat } from '@/modules/chat-matrix-fast/types/ChatMessage'
import { groupChatTypeLabel, resolveChatHeaderTitle } from '@/modules/chat-matrix-fast/constants/chat'
import { formatMatrixLastSeen } from '@/modules/chat-matrix-fast/services/matrixPresenceService'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { useReactiveNow } from '@/utils/useReactiveNow'

interface IContact {
  fullName?: string
  role?: string
  avatar?: string | null
}

interface IActiveChat {
  chat?: ChatOut | null
  contact?: IContact | null
}

const props = withDefaults(
  defineProps<{
    activeChat: IActiveChat | null
    /** Drawer (SlidingChat) vs embedded (EmbeddedChat) layout. */
    showBackButton?: boolean
    showHideButton?: boolean
    showMemberCount?: boolean
    showTypePrefix?: boolean
    typingText?: string
  }>(),
  {
    showBackButton: true,
    showHideButton: false,
    showMemberCount: true,
    showTypePrefix: true,
    typingText: '',
  },
)

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'hideChat'): void
  (e: 'openEvent'): void
}>()

const isGroupLike = computed(() => {
  const chat = props.activeChat?.chat
  return !!(chat && isGroupLikeChat(chat))
})

const groupChat = computed(() => {
  const chat = props.activeChat?.chat
  return chat && isGroupChat(chat) ? chat : null
})

const eventChat = computed(() => {
  const chat = props.activeChat?.chat
  return chat && isEventChat(chat) ? chat : null
})

const groupIconSource = computed(() => {
  if (!props.activeChat?.chat || !isGroupLikeChat(props.activeChat.chat)) {
    return null
  }

  return props.activeChat.chat.icon
    || props.activeChat.contact?.avatar
    || null
})

const groupMatrixRoomId = computed(() => {
  const chat = props.activeChat?.chat
  if (!chat || !isGroupLikeChat(chat)) {
    return null
  }

  return (chat as ChatOut & { matrix_room_id?: string }).matrix_room_id ?? null
})

const groupRoomAvatar = useGroupRoomAvatar(groupIconSource, groupMatrixRoomId)

const headerAvatar = computed(() => {
  if (!props.activeChat) return null
  const chat = props.activeChat.chat
  if (chat && isGroupLikeChat(chat)) {
    // Prefer sticky groupRoom, but never start empty — list already has icon.
    return groupRoomAvatar.value || groupIconSource.value || null
  }
  return props.activeChat.contact?.avatar || null
})

const headerTitle = computed(() => {
  if (groupChat.value) {
    if (props.showTypePrefix) {
      return `${groupChatTypeLabel(groupChat.value.type)} - ${groupChat.value.title}`
    }

    return groupChat.value.title
  }

  if (eventChat.value) {
    return eventChat.value.title
  }

  return ''
})

const chatStoreCore = useChatStoreCore()
const presenceNow = useReactiveNow()

/** `total_members` с backend (currentChat → activeChat → list). */
const memberCount = computed(() => {
  const chat = (groupChat.value || eventChat.value) as (ChatOut & { total_members?: number; id?: number | string }) | null
  if (!chat) return 0

  const candidates = [
    chatStoreCore.currentChat?.id === chat.id ? chatStoreCore.currentChat.total_members : undefined,
    chat.total_members,
    chatStoreCore.chats.find(c => c.id === chat.id)?.total_members,
  ]

  for (const total of candidates) {
    if (typeof total === 'number' && total > 0) {
      return total
    }
  }

  return 0
})

const dialogStatusLabel = computed(() => {
  const contact = props.activeChat?.contact
  if (!contact) {
    return ''
  }

  if (props.typingText) {
    return props.typingText
  }

  const role = contact.role?.trim() || ''
  const presence = contact.online
    ? 'в сети'
    : formatMatrixLastSeen(
      chatStoreCore.currentChat?.id === props.activeChat?.chat?.id
        ? chatStoreCore.currentChat?.last_seen_at
        : null,
      presenceNow.value,
    )

  if (role && presence) {
    return `${role} (${presence})`
  }

  return role || presence
})

/** Шапка диалога: полное ФИО (с отчеством), список — без отчества */
const dialogHeaderName = computed(() => {
  const fallback = props.activeChat?.contact?.fullName || ''
  const chatId = props.activeChat?.chat?.id
  if (chatId == null) {
    return fallback
  }

  const coreChat = chatStoreCore.currentChat?.id === chatId
    ? chatStoreCore.currentChat
    : chatStoreCore.chats.find(c => c.id === chatId)

  return coreChat ? (resolveChatHeaderTitle(coreChat) || fallback) : fallback
})
</script>

<template>
  <div class="active-chat-header d-flex align-center text-medium-emphasis pa-4">
    <IconBtn
      v-if="showBackButton"
      class="me-4"
      @click="emit('back')"
    >
      <VIcon icon="ri-arrow-left-line" />
    </IconBtn>

    <!-- One persistent avatar instance — remount on group↔dialog was reloading the same S3 file. -->
    <VAvatar
      v-if="activeChat"
      size="40"
      :variant="!headerAvatar ? 'tonal' : undefined"
      :color="!headerAvatar ? 'primary' : undefined"
      :class="!headerAvatar ? getAvatarColor(headerTitle || activeChat.contact?.fullName || '') : []"
      class="me-4"
    >
      <AuthenticatedVImg
        v-if="headerAvatar"
        :src="headerAvatar"
        :alt="headerTitle || activeChat.contact?.fullName || ''"
      />
      <span v-else>{{ avatarText(headerTitle || activeChat.contact?.fullName || '') }}</span>
    </VAvatar>

    <template v-if="isGroupLike && (groupChat || eventChat)">
      <div class="flex-grow-1 overflow-hidden">
        <h6 class="text-h6 font-weight-regular text-truncate">
          {{ headerTitle }}
        </h6>
        <p
          v-if="typingText"
          class="text-body-2 text-primary text-truncate mb-0"
        >
          {{ typingText }}
        </p>
        <p
          v-else-if="showMemberCount && memberCount > 0"
          class="text-body-2 text-truncate mb-0"
        >
          {{ memberCount }} {{ memberCount === 1 ? 'участник' : memberCount >= 2 && memberCount <= 4 ? 'участника' : 'участников' }}
        </p>
      </div>
      <div
        v-if="eventChat?.event_url"
        class="event-chat-header-actions d-flex align-center gap-2 ms-auto flex-shrink-0"
      >
        <VBtn
          variant="tonal"
          color="primary"
          size="small"
          prepend-icon="ri-external-link-line"
          @click="emit('openEvent')"
        >
          Открыть задачу
        </VBtn>
      </div>
    </template>

    <template v-else-if="activeChat && activeChat.contact">
      <div class="flex-grow-1 overflow-hidden">
        <h6 class="text-h6 font-weight-regular">
          {{ dialogHeaderName }}
        </h6>
        <p class="text-body-2 text-truncate mb-0">
          <span :class="{ 'text-primary': Boolean(typingText) || activeChat.contact.online }">
            {{ dialogStatusLabel }}
          </span>
        </p>
      </div>
    </template>

    <VTooltip
      v-if="showHideButton"
      location="bottom"
      text="Скрыть чат"
    >
      <template #activator="{ props: tooltipProps }">
        <IconBtn
          v-bind="tooltipProps"
          class="ms-2"
          @click="emit('hideChat')"
        >
          <VIcon icon="ri-play-fill" />
        </IconBtn>
      </template>
    </VTooltip>
  </div>
</template>

<style lang="scss" scoped>
.active-chat-header {
  min-block-size: 64px;
  padding-inline: 1.25rem;
  background-color: rgb(var(--v-theme-background));
}
</style>
